import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import { getValue, setValue } from "@utils/lodash";
import { NavLink } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import NavigationBar from "@components/NotificationBar/index";
import DeleteModal from "@_common/deleteModal";
import { toast } from "react-toastify";
import ScrollableReactTable from "@components/ScrollableDatatable";
import PaginationPage from "@components/pagination/pagination";
import SearchHeader from "@components/common/SearchHeader";
import { adminDeleteSpecificOrganization } from "@services/client-list.service";

import Notfound from "@components/Not-Found";
import moment from "moment";
import {
  adminBulkLocationUpdateStatus,
  clientGetLocation,
  exportLocation,
} from "@services/location.service";
import Loader from "@components/common/Loader";
import { QueryRequestHelper } from "@_common/query-request-helper";
import NoAccessPage from "@components/common/NoAccess";
import { handleDownloadOnClick } from "@_common/downloadfile";
import { currentDate } from "@_common/date-helpers";
import FilterForm from "@components/common/AddFilter/FilterForm";
import {
  adminGetCitiesListManagement,
  adminGetCountriesListManagement,
  adminGetListManagementGroupCode,
  adminGetStatesListManagement,
} from "@services/list-management.service";
import { adminGetGroups } from "@services/groups.service";
import ConfigLocationNotfound from "@components/Page-Not-Found/config-location-not-found";
import MoreConfigSvgComponent from "../../assets/svg-components/left-menu/more-config";
import RightMenuHeaders from "@components/common/RightMenuHeaders";
import { countKeysWithValues } from "@_common/text-helpers";

interface ILocationsProps {}

const Locations: React.FunctionComponent<ILocationsProps> = (props: any) => {
  let navigate = useNavigate();

  /* -------------------------------------------------------------------------- */
  /*                               UseState Section                             */
  /* -------------------------------------------------------------------------- */

  const [isLoading, setIsLoading] = React.useState(true);
  const [list, setList] = React.useState<[]>([]);
  const [dropdownOptions] = useState([
    { value: "LOCATION", label: "Location" },
    // { value: "FUNCTION", label: "Function" },
  ]);
  /* -------------------------------------------------------------------------- */
  /*                               UseEffect Section                            */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length === 0 && props.permissionAPITriggered) {
      getData();
      getCountriesList();
      getRegionList();
      getGroupList();
    }
  }, [props.permissionAPITriggered]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length > 0 && props.permissionAPITriggered) {
      getData();
      getCountriesList();

      getRegionList();
      getGroupList();

      if (params.page_no) {
        setPage_no(parseInt(params.page_no));
      }
      if (params.search_text) {
        setSearchInput(params.search_text);
        setShowSearchTextbox(true);
      }
      if (params.limit) {
        setLimit(parseInt(params.limit));
      }

      setRequest({
        ...request,
        country_id: getValue(params, `country_id`, ""),
        state_id: getValue(params, `state_id`, ""),
        city_id: getValue(params, `city_id`, ""),
        region_id: getValue(params, `region_id`, ""),
        group_id: getValue(params, `group_id`, ""),
      });

      if (getValue(params, `country_id`, "")) {
        setFilterVisibility(true);
        getStatesList(getValue(params, `country_id`, ""));
      }
    }
  }, [window.location.href, props.permissionAPITriggered]);

  /* -------------------------------------------------------------------------- */
  /*                               API Section                                  */
  /* -------------------------------------------------------------------------- */

  const getData = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    let payload: any = {
      page_no: params.page_no ? parseInt(params.page_no) : 1,
      page_size: params.limit ? parseInt(params.limit) : limit,
      search_text: params.search_text,
      // status: "active",
      is_active: params.is_active,
      sort_by: params.sort_by,
      country_id: getValue(params, `country_id`, ""),
      state_id: getValue(params, `state_id`, ""),
      city_id: getValue(params, `city_id`, ""),
      region_id: getValue(params, `region_id`, ""),
      group_id: getValue(params, `group_id`, ""),
    };
    let queryRequest = QueryRequestHelper(payload);
    try {
      setIsLoading(true);
      let resp = await clientGetLocation(queryRequest);
      if (resp) {
        setList(
          getValue(resp, `data.locations`, []).map((item: object) => ({
            ...item,
            created_at: moment(getValue(item, `created_at`, "")).format(
              "DD MMM YYYY"
            ),
            status: getValue(item, `is_active`, false) ? "Active" : "Inactive",
            city_name: getValue(item, `city.name`, ""),
          }))
        );
        setTotalCount(getValue(resp, `data.pagination.total`, 0));
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               Search section                               */
  /* -------------------------------------------------------------------------- */
  const [showSearchTextbox, setShowSearchTextbox] = useState(false);
  const [search_text, setSearchInput] = useState<any>("");

  const toggleSearch = () => {
    setShowSearchTextbox((prevState) => !prevState);
  };
  const toggleSearchClose = () => {
    setSearchInput("");
    toggleSearch();
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (params.search_text) {
      delete params.search_text;
      let payload: any = { ...params };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };

  const handleChangeSearch = async (e: any) => {
    setSearchInput(e.target.value);
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      page_no: 1,
      search_text: e.target.value,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    if (!e.target.value) {
      getData();
    }
  };

  const [selectedIDs, setSelectedIDs] = useState<any>([]);
  const getTableState = (value: object) => {
    let selectedIndexList = Object.keys(
      getValue(value, `selectedRowIds`, {})
    ).map((item) => parseInt(item));
    let selectedIds =
      list.length > 0
        ? list.filter((_item: object, index: number) =>
            selectedIndexList.includes(index)
          )
        : [];
    if (selectedIds.length > 0) {
      setSelectedIDs(
        selectedIds.map((item: object) => getValue(item, `id`, ""))
      );
    } else {
      setSelectedIDs([]);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                 Delete section                             */
  /* -------------------------------------------------------------------------- */

  const [deleteValue, setDeleteValue] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const handleDeleteFun = (id: string, value: string) => {
    setDeleteValue(value);
    setDeleteId(id);
    handleDelete();
  };
  const [isDelete, setIsDelete] = useState(false);
  const handleDelete = () => {
    setIsDelete(!isDelete);
  };

  const handleDeleteFunction = async () => {
    try {
      let resp = await adminDeleteSpecificOrganization(deleteId);
      if (resp) {
        toast.success(getValue(resp, `message`, ""));
        handleDelete();
        getData();
      }
    } catch (error) {}
  };

  /* -------------------------------------------------------------------------- */
  /*                               Onchange section                             */
  /* -------------------------------------------------------------------------- */

  const handleChangeSortBy = (value: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    params.sort_by = value;
    props.history.push({
      pathname: window.location.pathname,
      query: params,
    });
  };

  const handleChangeStatus = (value: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    params.status = value;
    props.history.push({
      pathname: window.location.pathname,
      query: params,
    });
  };
  /* -------------------------------------------------------------------------- */
  /*                               Pagination section                           */
  /* -------------------------------------------------------------------------- */

  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(100);
  const [page_no, setPage_no] = useState(1);

  const handleChangePagination = (page_no: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload = {
      ...params,
      page_no: page_no,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  const handleChangeLimit = (limit: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      limit: limit,
      page_no: "1",
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/add-clientlist/profile/${id}`);
  };
  const rowClick = (obj: object) => {
    // console.log(getValue(obj,`original`,''))
    navigate(
      `/location/detail/${getValue(obj, `original.id`, "")}?${mainQueryRequest}`
    );
  };
  const getTrProps = (rowInfo: any) => {
    if (getValue(rowInfo, `id`, "")) {
      return {
        style: {
          //   background:  'red',
          //   color: 'black'
        },
      };
    }
    return {};
  };
  const columns = React.useMemo(
    () => [
      {
        Header: "Location ID",
        accessor: "location_id",
        width: 150,
        Cell: ({ row }: any) => {
          return (
            <NavLink
              to={`/location/detail/${row.original.id}?${mainQueryRequest}`}
            >
              {row.values.location_id}
            </NavLink>
          );
        },
      },
      {
        Header: "Location Name",
        accessor: "location_name",
        width: 300,
      },
      {
        Header: "Entity Name",
        accessor: "entity_name",
        width: 250,
      },
      {
        Header: "City",
        accessor: "city_name",
        width: 140,
      },
      {
        Header: "# Status",
        accessor: "status",
        width: 120,
      },
    ],
    []
  );

  const handleChangeActiveStatus = async (value: string) => {
    try {
      let resp = await adminBulkLocationUpdateStatus({
        ids: selectedIDs,
        is_active: value === "active" ? true : false,
      });
      if (resp) {
        toast.success(getValue(resp, `message`, ""));
        setSelectedIDs([]);
        getData();
      } else {
        getData();
      }
    } catch (error) {}
  };

  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const [check, setCheck] = useState(false);
  const handleRemoveNavItems = () => {
    setCheck(!check);
    setSelectedIDs([]);
  };

  /* -------------------------------------------------------------------------- */
  /*                             Download Reports                               */
  /* -------------------------------------------------------------------------- */
  const [downloadLoading, setDownloadLoading] = useState(false);
  const handleDownloadSelfComplianceReport = async () => {
    let payload: any = {
      report_type: "LOCATION",
      report_name: `location_report_${currentDate()}`,
      country_id: getValue(request, `country_id`, ""),
      state_id: getValue(request, `state_id`, ""),
      city_id: getValue(request, `city_id`, ""),
      region_id: getValue(request, `region_id`, ""),
      group_id: getValue(request, `group_id`, ""),
    };
    Object.keys(payload).forEach((key) => {
      if (!payload[key] || payload[key] === undefined) {
        delete payload[key];
      }
    });
    setDownloadLoading(true);
    try {
      let resp = await exportLocation({
        ...payload,
      });
      if (resp) {
        setDownloadLoading(false);
        toast.success(getValue(resp, `message`, ""));
        navigate(`/self-compliance-logs/LOCATION`);
      } else {
        setDownloadLoading(false);
      }
    } catch (error) {
      setDownloadLoading(false);
    }
  };
  /* -------------------------------------------------------------------------- */
  /*                        smart Fileter section                               */
  /* -------------------------------------------------------------------------- */
  const [request, setRequest] = useState<any>({
    country_id: "101",
    state_id: "",
    city_id: 0,
    region_id: 0,
    group_id: "",
  });

  const [isFilterVisible, setFilterVisibility] = React.useState(false);
  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const handleFilter = () => {
    setFilterIsOpen(!filterIsOpen);
  };

  useEffect(() => {
    if (request.country_id) {
      getStatesList(request.country_id);
    }
    if (request.state_id) {
      getCitiesList(request.state_id);
    }
  }, [request.country_id, request.state_id]);

  const [countryList, setCountryList] = useState([]);
  const getCountriesList = async () => {
    try {
      let resp = await adminGetCountriesListManagement();
      if (resp) {
        setCountryList(
          getValue(resp, `data.countries`, []).map((item: object) => ({
            ...item,
            value: getValue(item, `name`, ""),
            label: getValue(item, `name`, ""),
          }))
        );
      } else {
      }
    } catch (error) {}
  };
  const [states, setStates] = useState([]);
  const getStatesList = async (countryId: number) => {
    if (countryId) {
      try {
        let resp = await adminGetStatesListManagement(countryId);
        if (resp) {
          setStates(
            getValue(resp, `data.states`, []).map((item: object) => ({
              ...item,
              value: getValue(item, `name`, ""),
              label: getValue(item, `name`, ""),
            }))
          );
        } else {
        }
      } catch (error) {}
    }
  };
  const [citiesList, setCityList] = useState([]);
  const getCitiesList = async (stateId: number) => {
    if (stateId) {
      try {
        let resp = await adminGetCitiesListManagement(stateId);
        if (resp) {
          // console.log(resp);
          setCityList(
            getValue(resp, `data.cities`, []).map((item: object) => ({
              ...item,
              value: getValue(item, `name`, ""),
              label: getValue(item, `name`, ""),
            }))
          );
        } else {
        }
      } catch (error) {}
    } else {
      setCityList([]);
    }
  };
  const [regionTypeList, setRegionTypeList] = useState([]);
  const getRegionList = async () => {
    try {
      let resp = await adminGetListManagementGroupCode("region-type");
      if (resp) {
        let list =
          getValue(resp, `data.listItems.length`, 0) > 0
            ? getValue(resp, `data.listItems`, []).filter((item: object) =>
                getValue(item, `is_active`, "")
              )
            : [];
        setRegionTypeList(
          list.map((item: object) => ({
            id: getValue(item, `id`, ""),
            is_active: getValue(item, `is_active`, ""),
            text: getValue(item, `text`, ""),
            value: getValue(item, `text`, ""),
            label: getValue(item, `text`, ""),
          }))
        );
      } else {
      }
    } catch (error) {}
  };
  const [groupList, setGroupList] = useState([]);
  const getGroupList = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    let payload: any = {
      page_no: 1,
      page_size: 100,
    };
    let queryRequest = QueryRequestHelper(payload);
    try {
      let resp = await adminGetGroups(queryRequest);
      if (resp) {
        let list =
          getValue(resp, `data.groups.length`, 0) > 0
            ? getValue(resp, `data.groups`, []).filter((item: object) =>
                getValue(item, `is_active`, "")
              )
            : [];
        setGroupList(
          list.map((item: object) => ({
            ...item,
            value: getValue(item, `group_name`, ""),
            label: getValue(item, `group_name`, ""),
          }))
        );
      } else {
      }
    } catch (error) {}
  };
  const reset = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      page_no: params.page_no,
      search_text: getValue(params, `search_text`, ""),
      limit: getValue(params, `limit`, ""),
    };
    setRequest({
      ...request,
      country_id: getValue(request, `country_id`, ""),
      state_id: "",
      city_id: "",
      region_id: "",
      group_id: "",
    });
    setFilterVisibility(false);
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    getData();
  };

  const handleChangeSelect = (e: any, type: string) => {
    if (type === "country_id") {
      getStatesList(getValue(e, `id`, ""));
      setRequest({
        ...request,
        country_id: getValue(e, `id`, ""),
      });
    }
    if (type === "state_id") {
      getCitiesList(getValue(e, `id`, ""));
      setRequest({
        ...request,
        state_id: getValue(e, `id`, ""),
      });
    }
    if (type === "city_id") {
      setRequest({
        ...request,
        city_id: getValue(e, `id`, ""),
      });
    }
    if (type === "region_id") {
      setRequest({
        ...request,
        region_id: getValue(e, `id`, ""),
      });
    }
    if (type === "group_id") {
      setRequest({
        ...request,
        group_id: getValue(e, `id`, ""),
      });
    }
  };

  const handleSubmitSmartFilter = () => {
    if (
      getValue(request, `country_id`, "")
      // getValue(request, `state_id`, "")
      // && getValue(request, `category_of_law_id`, "") &&
      // getValue(request, `legislation_id`, "") &&
      // getValue(request, `legislation_rule_id`, "")
    ) {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(urlSearchParams.entries());
      let payload: any = {
        ...params,
        page_no: params.page_no,
        country_id: getValue(request, `country_id`, ""),
        state_id: getValue(request, `state_id`, ""),
        city_id: getValue(request, `city_id`, ""),
        region_id: getValue(request, `region_id`, ""),
        group_id: getValue(request, `group_id`, ""),
      };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      handleFilter();
    } else {
      toast.error("All fields are mandatory");
    }
  };
  let country_ID =
    getValue(countryList, `length`, 0) > 0 &&
    countryList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `country_id`, "")
    );
  let state_ID =
    getValue(states, `length`, 0) > 0 &&
    states.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `state_id`, "")
    );

  let city_ID =
    getValue(citiesList, `length`, 0) > 0 &&
    citiesList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `city_id`, "")
    );

  let region_ID =
    getValue(regionTypeList, `length`, 0) > 0 &&
    regionTypeList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `region_id`, "")
    );

  let group_ID =
    getValue(groupList, `length`, 0) > 0 &&
    groupList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `group_id`, "")
    );
  const urlParamsFun = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let request = QueryRequestHelper({ ...params });
    return request;
  };

  const mainQueryRequest = useMemo(
    () => urlParamsFun(),
    [window.location.href]
  );

  const handleNavigateLogs = () => {
    navigate(`/file-logs/LOCATION`);
  };
  return (
    <DashboardLayout
      permissions={getValue(props, `tabPermissionList`, [])}
      subTabPermissionList={getValue(props, `subTabPermissionList`, [])}
    >
      {!isLoading &&
      !getValue(props, `subTabPermissionList`, []).includes(
        "configuration_location"
      ) ? (
        <NoAccessPage />
      ) : (
        <>
          {getValue(selectedIDs, `length`, 0) > 0 && (
            <NavigationBar
              selectedIDs={selectedIDs}
              isMarkInactiveSectionVisible={
                getValue(props, `permissions`, []).includes("update")
                  ? true
                  : false
              }
              handleChangeActiveStatus={handleChangeActiveStatus}
              text={"MARK INACTIVE"}
              multi={
                getValue(props, `permissions`, []).includes("update")
                  ? true
                  : false
              }
              handleRemoveNavItems={handleRemoveNavItems}
            />
          )}
          {/* <div className="dashboard-wrapper__header">
            <h4 className="dashbaord-wrapper__header-title">Locations</h4>
          </div> */}

          <div className="dashboard-wrapper__header d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="header-parent-icon-holder">
                <MoreConfigSvgComponent />
              </div>

              <div className="title-wrapper">
                <h6 className="dashbaord-wrapper__header-parent-title">
                  Configuration
                </h6>
                <h4 className="dashbaord-wrapper__header-title">Locations</h4>
              </div>
            </div>

            <div className="d-flex">
              <RightMenuHeaders
                route={`/location/detail?${mainQueryRequest}`}
                //search
                isSearch={true}
                searchInput={search_text}
                showSearchTextbox={showSearchTextbox}
                toggleSearch={toggleSearch}
                toggleSearchClose={toggleSearchClose}
                handleChangeSearch={handleChangeSearch}
                //smartFilter
                smartFilter={true}
                handleFilter={handleFilter}
                add={
                  getValue(props, `permissions`, []).includes("create")
                    ? "true"
                    : "false"
                }
                commonUpload={
                  getValue(props, `permissions`, []).includes("update")
                    ? true
                    : false
                }
                dropdownOptions={dropdownOptions}
                //download section
                downloadSelfComplianceReport={true}
                // viewDownloadedSelfComplianceReports={true}
                handleDownloadSelfComplianceReport={
                  handleDownloadSelfComplianceReport
                }
                downloadLoading={downloadLoading}
                viewDownloadedSelfComplianceReports={true}
                handleNavigateLogs={handleNavigateLogs}
                //Line Seperator of button
                lineSeperator={true}
                selectedFilterCount={countKeysWithValues(params)}
              />
            </div>
          </div>

          <div
            className={`main-page-layout-wrapper`}
            id={
              getValue(selectedIDs, `length`, 0) > 0
                ? "blueNavigationVisible"
                : ""
            }
          >
            <div className="admin-wrapper position-relative">
              <div className="admin-wrapper position-relative d-none">
                <SearchHeader
                  route={`/location/detail?${mainQueryRequest}`}
                  searchInput={search_text}
                  showSearchTextbox={showSearchTextbox}
                  toggleSearch={toggleSearch}
                  toggleSearchClose={toggleSearchClose}
                  handleChangeSearch={handleChangeSearch}
                  handleDownloadSelfComplianceReport={
                    handleDownloadSelfComplianceReport
                  }
                  downloadLoading={downloadLoading}
                  // isBulkUploadVisible={
                  // 	getValue(props, `permissions`, []).includes("update")
                  // 		? true
                  // 		: false
                  // }
                  add={
                    getValue(props, `permissions`, []).includes("create")
                      ? "true"
                      : "false"
                  }
                  // locationUpload={true}
                  commonUpload={
                    getValue(props, `permissions`, []).includes("update")
                      ? true
                      : false
                  }
                  dropdownOptions={dropdownOptions}
                  downloadSelfComplianceReport
                  smartFilter={true}
                  setFilterFormVisibility={() => {
                    setFilterVisibility(!isFilterVisible);
                  }}
                  isFilterVisible={isFilterVisible}
                  filterIsOpen={filterIsOpen}
                  handleFilter={() => {
                    setFilterIsOpen(!filterIsOpen);
                  }}
                />
              </div>
              {/* {isFilterVisible && ( */}
              <FilterForm
                setFilterFormVisibility={() => {
                  setFilterVisibility(!isFilterVisible);
                }}
                countryList={countryList}
                states={states}
                citiesList={citiesList}
                regionList={regionTypeList}
                groupList={groupList}
                handleChangeSelect={handleChangeSelect}
                reset={reset}
                handleSubmitSmartFilter={handleSubmitSmartFilter}
                events={false}
                location={true}
                additionalFilter={false}
                country_ID={country_ID}
                state_ID={state_ID}
                city_ID={city_ID}
                region_ID={region_ID}
                group_ID={group_ID}
                filterIsOpen={filterIsOpen}
                handleFilter={handleFilter}
              />
              {/* )} */}
              {isLoading || !props.permissionAPITriggered ? (
                <Loader />
              ) : list.length > 0 ? (
                <ScrollableReactTable
                  columns={columns}
                  data={list}
                  getTableState={getTableState}
                  rowClick={rowClick}
                  check={check}
                  selectedIDs={selectedIDs}
                  getTrProps={getTrProps}
                />
              ) : (
                <>
                  {Object.keys(params).length !== 0 ? (
                    <>
                      <ConfigLocationNotfound />
                    </>
                  ) : (
                    <ConfigLocationNotfound />
                  )}
                </>
              )}
            </div>
            <div className="footer-wrapper d-flex justify-content-between">
              {list.length > 0 && (
                <img
                  src="/images/building-illustration.svg"
                  className="img-fluid"
                />
              )}
              {totalCount > 10 ? (
                <PaginationPage
                  totalCount={totalCount}
                  limit={limit}
                  pageNumber={page_no}
                  handleChangePagination={handleChangePagination}
                  handleChangeLimit={handleChangeLimit}
                />
              ) : (
                <p className="show-rows-count mt-3 d-flex">
                  Total Count:&nbsp;&nbsp;{" "}
                  <p style={{ color: "rgb(13, 110, 253)" }}>{totalCount}</p>
                </p>
              )}
            </div>
            <DeleteModal
              isOpen={isDelete}
              handleModal={handleDelete}
              handleSubmit={handleDeleteFunction}
              deleteValue={deleteValue}
            />
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

export default Locations;
